import { useCallback, useState } from 'react';
import memoizeOne from 'memoize-one';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/IssueCreateProvider.tsx';
import type { ExpandedItems } from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import type { JiraProject } from '@atlassian/jira-business-entity-common/src/types.tsx';
import type { Column } from '../../../common/types/base-table.tsx';
import {
	isItemAnInlineCreate,
	isItemShowMore,
} from '../../../common/utils/row-placeholder/index.tsx';
import type { ListViewControllerData } from '../../../controllers/list-view-issues/types.tsx';
import { isEmptyRow } from '../../../controllers/list-view-issues/utils.tsx';
import type { ExpandedGroups } from '../../../services/get-item-groups/types.tsx';

/**
 * Calculates and returns the visibility indexes for each column in a table.
 * This function uses memoization to optimize performance.
 *
 * @param {number} scrollLeft - The current horizontal scroll position of the table.
 * @param {number} tableWidth - The width of the table.
 * @param {Column<ListViewControllerData>[]} columns - An array of columns in the table.
 *
 * @returns {Object} An object containing two arrays: `visibleColumns` and `invisibleColumns`.
 * Each array contains the indexes of the visible and invisible columns respectively.
 *
 * @deprecated Use useIsColumnVisible instead
 */
export const getColumnVisibilityIndexes = memoizeOne(
	(scrollLeft: number, tableWidth: number, columns: Column<ListViewControllerData>[]) => {
		const leftEdge = scrollLeft;
		const rightEdge = scrollLeft + tableWidth;

		const visibleColumnsIdx: Array<number> = [];
		const invisibleColumnsIdx: Array<number> = [];

		let totalOffset = 0;
		const columnsOffset = columns.reduce<number[]>((acc, column) => {
			acc.push(totalOffset);
			totalOffset += column.width;
			return acc;
		}, []);

		columnsOffset.forEach((columnOffset, colIdx) => {
			const isNotVisible =
				columnOffset + columns[colIdx].width < leftEdge || columnOffset > rightEdge;

			isNotVisible ? invisibleColumnsIdx.push(colIdx) : visibleColumnsIdx.push(colIdx);
		});

		return {
			visibleColumns: visibleColumnsIdx,
			invisibleColumns: invisibleColumnsIdx,
		};
	},
);

export const useHighlightDoneItems = () => {
	const [highlightedIssues, setHightlightedIssues] = useState<{
		[key: string]: boolean;
	}>({});

	const toggleHighlightRow = useCallback((issueKey: string, value: boolean, timeout = 5000) => {
		setHightlightedIssues((prev) => ({ ...prev, [issueKey]: value }));

		if (value) {
			// using a timer here to clean up the object instead of tiying it to the unmount of the row because base-table unmounts rows due to virtualisation.
			setTimeout(() => {
				setHightlightedIssues((prevState) => {
					const newState = { ...prevState };
					delete newState[issueKey];
					return newState;
				});
			}, timeout);
		}
	}, []);

	const getRowClassName = useCallback(
		({ rowData }: { rowData: ListViewControllerData }) =>
			highlightedIssues[rowData.key] && 'highlight',
		[highlightedIssues],
	);

	return {
		toggleHighlightRow,
		getRowClassName,
	};
};

export const calculateIssuesHeight = (
	issues: ListViewControllerData[],
	rowHeight: number,
	expandedItems: undefined | ExpandedItems | ExpandedItems,
	expandedGroups: ExpandedItems | ExpandedGroups,
) =>
	issues.reduce((totalHeight, issue) => {
		let accumulatedHeight = totalHeight;

		accumulatedHeight += rowHeight;

		// add the total heights of all expanded children
		if (
			issue.children &&
			issue.children.length > 0 &&
			((expandedItems && expandedItems[toIssueId(issue.issueId)] === true) ||
				(expandedGroups && expandedGroups[toIssueId(issue.issueId)] === true))
		) {
			accumulatedHeight += calculateIssuesHeight(
				issue.children,
				rowHeight,
				expandedItems,
				expandedGroups,
			);
		}

		return accumulatedHeight;
	}, 0);

export const getIssueCreateRankBefore = ({
	items,
	startIndex,
}: {
	items: ListViewControllerData[];
	startIndex: number;
}) => {
	let rankBefore;

	const itemsCount = items?.length ?? 0;
	if (itemsCount > 0) {
		for (let i = startIndex; i < itemsCount; i++) {
			const { issueId, rank } = items[i];

			const isItemAnIssue =
				!isOptimisticIssue(issueId) &&
				!isEmptyRow(String(issueId)) &&
				!isItemAnInlineCreate(String(issueId)) &&
				!isItemShowMore(String(issueId));

			if (isItemAnIssue) {
				rankBefore = { issueId: String(issueId), rank };
				break;
			}
		}
	}

	return rankBefore;
};

// permission check for inline status group by config
export const getIsInlineGroupByStatusConfigEnabled = ({
	project,
	isGroupingByStatus,
	isGrouping,
}: {
	project: JiraProject;
	isGrouping: boolean;
	isGroupingByStatus: boolean;
}) => {
	const isAdmin = project.permissions?.administerProject ?? false;
	const isTMP = project.isSimplified ?? false;

	return isAdmin && isTMP && isGrouping && isGroupingByStatus;
};

export const getIsInlineGroupConfigExperimentEnabled = () => {
	const [expConfig] = UNSAFE_noExposureExp('jira_inline_status_group_config_in_list_view');
	return expConfig.get('isInlineStatusConfigEnabled', false);
};

export const getTotalfooterHeight = ({
	isGrouping,
	canCreateIssues,
	isInlineGroupByStatusConfigEnabled,
}: {
	isGrouping: boolean;
	canCreateIssues: boolean;
	isInlineGroupByStatusConfigEnabled: boolean;
}) => {
	const isGroupingWithoutFooter =
		isInlineGroupByStatusConfigEnabled && getIsInlineGroupConfigExperimentEnabled()
			? false
			: isGrouping;
	return isGroupingWithoutFooter || !canCreateIssues ? 0 : 40;
};

export const getIsAiwbInListViewEnabled = () => {
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('aiwb_in_list_view_experiment');
	return expConfig.get('isEnabled', false);
};
